import { Link } from "@tanstack/react-router";
import { ArrowRight, BatteryCharging, MapPin, Snowflake, Wrench } from "lucide-react";
import { services, areas } from "@/lib/site-config";

interface Props {
  /** Slug of the current service to exclude from the sibling list (e.g. "mot"). */
  currentService?: string;
  /** Slug of the current area to exclude. */
  currentArea?: string;
  /** When provided, area links go to the service-in-area page instead of the area hub. */
  serviceForAreaLinks?: string;
  title?: string;
  /** Show the services hub link at the top. */
  showHubLink?: boolean;
}

const AREA_SLUGS = ["poplar", "canary-wharf", "isle-of-dogs", "limehouse", "docklands", "tower-hamlets"];

export function InternalLinksBlock({
  currentService,
  currentArea,
  serviceForAreaLinks,
  title = "Explore more",
  showHubLink = true,
}: Props) {
  const otherServices = services.filter((s) => s.slug !== currentService);
  const areaList = areas.filter((a) => AREA_SLUGS.includes(a.slug) && a.slug !== currentArea);

  return (
    <section className="py-12 md:py-16 border-t border-border">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-xl md:text-2xl font-bold text-foreground text-center">{title}</h2>

        {showHubLink && (
          <div className="mt-3 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              View all services <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sibling services */}
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-foreground">
              <Wrench className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-bold uppercase tracking-wide">Other services</h3>
            </div>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {otherServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}` as any}
                    className="flex items-center justify-between rounded-lg border border-border/70 bg-background/60 px-3 py-2 text-sm font-semibold text-foreground hover:border-primary/50 hover:text-primary transition-colors"
                  >
                    <span>{s.name}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-primary" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Local areas */}
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-bold uppercase tracking-wide">
                {serviceForAreaLinks ? "Available across" : "Local areas we cover"}
              </h3>
            </div>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {areaList.map((a) =>
                serviceForAreaLinks ? (
                  <li key={a.slug}>
                    <Link
                      to="/areas/$area/$service"
                      params={{ area: a.slug, service: serviceForAreaLinks }}
                      className="flex items-center justify-between rounded-lg border border-border/70 bg-background/60 px-3 py-2 text-sm font-semibold text-foreground hover:border-primary/50 hover:text-primary transition-colors"
                    >
                      <span>
                        {services.find((s) => s.slug === serviceForAreaLinks)?.name || "Service"} in {a.name}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-primary" />
                    </Link>
                  </li>
                ) : (
                  <li key={a.slug}>
                    <Link
                      to="/areas/$area"
                      params={{ area: a.slug }}
                      className="flex items-center justify-between rounded-lg border border-border/70 bg-background/60 px-3 py-2 text-sm font-semibold text-foreground hover:border-primary/50 hover:text-primary transition-colors"
                    >
                      <span>Garage in {a.name}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-primary" />
                    </Link>
                  </li>
                ),
              )}
            </ul>
          </div>
        </div>

        {/* Featured specialist services — not in the standard services list */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/services/ev-hybrid-servicing"
            className="group flex items-center gap-3 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-card p-4 hover:border-primary/50 transition-colors"
          >
            <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <BatteryCharging className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">EV &amp; Hybrid Servicing</p>
              <p className="text-xs text-muted-foreground">Tesla, Polestar, Toyota hybrid, PHEV — routine servicing &amp; safety.</p>
            </div>
            <ArrowRight className="h-4 w-4 text-primary shrink-0" />
          </Link>
          <Link
            to="/services/air-con-regas"
            className="group flex items-center gap-3 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-card p-4 hover:border-primary/50 transition-colors"
          >
            <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Snowflake className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">Air Con Regas &amp; Service</p>
              <p className="text-xs text-muted-foreground">Independent prices, free initial check, anti-bac treatment.</p>
            </div>
            <ArrowRight className="h-4 w-4 text-primary shrink-0" />
          </Link>
        </div>
      </div>
    </section>
  );
}
